import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, X, Loader2, Pause, Play, Mountain, Home } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import ImageUploader from "../../components/ImageUploader";
import { resolveImg } from "../../lib/api";

const EMPTY = {
  title: "", subtitle: "", description: "", type: "apartment", active: true,
  localita: "dimaro-folgarida", tipologia: "", caratteristica: "", slug: "",
  max_guests: 4, bedrooms: 1, bathrooms: 1,
  amenities: [], house_rules: [], images: [], panoramas: [], room_types: [], smoobu_id: "", base_price: 50,
  cin: "", address: "", latitude: "", longitude: "", whatsapp_template: "",
  paypal_client_id: "", paypal_secret: "", paypal_mode: "sandbox", stripe_secret_key: "",
  guest_info: { wifi_name: "", wifi_password: "", keybox_code: "", checkin_instructions: "", parking_info: "", other_info: "" },
};

const NEW_ROOM = () => ({
  id: (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random())),
  name: "", max_guests: 2, quantity: 1, base_price: 55, min_price: 40, max_price: 200, smoobu_id: "", images: [], amenities: [],
});

export default function AdminApartments() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/apartments").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const open = (apt) => {
    setEditing(apt?.id || "new");
    setForm(apt ? { ...EMPTY, ...apt } : { ...EMPTY });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        amenities: Array.isArray(form.amenities) ? form.amenities : String(form.amenities).split(",").map((x) => x.trim()).filter(Boolean),
        house_rules: Array.isArray(form.house_rules) ? form.house_rules : String(form.house_rules).split(",").map((x) => x.trim()).filter(Boolean),
        images: Array.isArray(form.images) ? form.images : String(form.images).split(",").map((x) => x.trim()).filter(Boolean),
        panoramas: Array.isArray(form.panoramas) ? form.panoramas : [],
        room_types: form.type === "hotel" ? (form.room_types || []) : [],
        address: form.address || "",
        latitude: form.latitude === "" || form.latitude === undefined || form.latitude === null ? null : Number(form.latitude),
        longitude: form.longitude === "" || form.longitude === undefined || form.longitude === null ? null : Number(form.longitude),
      };
      if (editing === "new") await api.post("/admin/apartments", payload);
      else await api.put(`/admin/apartments/${editing}`, payload);
      toast.success("Struttura salvata");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa struttura?")) return;
    await api.delete(`/admin/apartments/${id}`); toast.success("Eliminata"); load();
  };

  const toggleActive = async (apt) => {
    try {
      await api.patch(`/admin/apartments/${apt.id}/active`, { active: !(apt.active !== false) });
      toast.success(apt.active !== false ? "Struttura messa in pausa (nascosta dal sito)" : "Struttura riattivata");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const csv = (v) => (Array.isArray(v) ? v.join(", ") : v || "");

  const updateRoom = (idx, patch) => setForm((f) => ({ ...f, room_types: f.room_types.map((r, i) => i === idx ? { ...r, ...patch } : r) }));
  const setGI = (patch) => setForm((f) => ({ ...f, guest_info: { ...(f.guest_info || {}), ...patch } }));
  const addRoom = () => setForm((f) => ({ ...f, room_types: [...(f.room_types || []), NEW_ROOM()] }));
  const removeRoom = (idx) => setForm((f) => ({ ...f, room_types: f.room_types.filter((_, i) => i !== idx) }));

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-4xl text-fir">Strutture</h1>
          <p className="text-fir-deep/60">Hotel (con tipologie di camera) e appartamenti indipendenti. Metti in pausa per nasconderli dal sito.</p>
        </div>
        <button data-testid="add-apartment-btn" onClick={() => open(null)} className="gold-btn px-5 py-3 rounded-xl flex items-center gap-2"><Plus size={18} /> Nuova</button>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {list.map((a) => {
          const paused = a.active === false;
          return (
          <div key={a.id} data-testid="admin-apartment-row" className={`bg-white rounded-2xl border ${paused ? "border-amber-300 opacity-80" : "border-stone"} overflow-hidden flex`}>
            <img src={resolveImg(a.images?.[0])} alt="" className="w-32 h-full object-cover" />
            <div className="p-4 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full flex items-center gap-1 ${a.type === "hotel" ? "bg-fir text-ivory" : "bg-stone text-fir-deep"}`}>
                  {a.type === "hotel" ? <><Mountain size={10} /> Hotel</> : <><Home size={10} /> Appartamento</>}
                </span>
                {paused && <span data-testid="paused-badge" className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">In pausa</span>}
              </div>
              <h3 className="font-display text-xl text-fir">{a.title}</h3>
              <p className="text-sm text-fir-deep/60">
                {a.type === "hotel" ? `${(a.room_types || []).length} tipologie camera` : `da €${a.from_price ?? a.base_price}/notte`} · Smoobu: {a.smoobu_id || "—"}
              </p>
              <div className="flex gap-3 mt-3 flex-wrap">
                <button onClick={() => open(a)} className="text-fir hover:text-gold flex items-center gap-1 text-sm"><Pencil size={14} /> Modifica</button>
                <button data-testid="toggle-active-btn" onClick={() => toggleActive(a)} className="text-fir hover:text-gold flex items-center gap-1 text-sm">
                  {paused ? <><Play size={14} /> Riattiva</> : <><Pause size={14} /> Metti in pausa</>}
                </button>
                <button data-testid="delete-apartment-btn" onClick={() => remove(a.id)} className="text-red-500 hover:text-red-700 flex items-center gap-1 text-sm"><Trash2 size={14} /> Elimina</button>
              </div>
            </div>
          </div>
        );})}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-fir-deep/70 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-ivory rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-y-auto p-7" data-lenis-prevent onClick={(e) => e.stopPropagation()} data-testid="apartment-form">
            <div className="flex justify-between items-center mb-5">
              <h2 className="font-display text-3xl text-fir">{editing === "new" ? "Nuova struttura" : "Modifica struttura"}</h2>
              <button onClick={() => setEditing(null)}><X className="text-fir" /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Tipo di struttura</Label>
                <select data-testid="apt-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="input-alpine w-full mt-1">
                  <option value="apartment">Appartamento</option>
                  <option value="hotel">Hotel (con tipologie di camera)</option>
                </select>
              </div>
              <TextField label="Titolo" span value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="apt-title" />
              <TextField label="Sottotitolo" span value={form.subtitle} onChange={(v) => setForm({ ...form, subtitle: v })} />
              <div className="sm:col-span-2 grid sm:grid-cols-2 gap-3 bg-stone/30 rounded-xl p-3">
                <div className="sm:col-span-2 text-xs uppercase tracking-wide text-fir-deep/60">URL SEO (slug appartamento)</div>
                <div>
                  <label className="text-xs text-fir-deep/60">Località</label>
                  <select data-testid="apt-localita" value={form.localita || "dimaro-folgarida"} onChange={(e) => setForm({ ...form, localita: e.target.value })} className="input-alpine w-full mt-0.5">
                    <option value="dimaro-folgarida">dimaro-folgarida</option>
                    <option value="val-di-sole">val-di-sole</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-fir-deep/60">Tipologia</label>
                  <select data-testid="apt-tipologia" value={form.tipologia || ""} onChange={(e) => setForm({ ...form, tipologia: e.target.value })} className="input-alpine w-full mt-0.5">
                    <option value="">—</option>
                    <option value="monolocale">monolocale</option>
                    <option value="bilocale">bilocale</option>
                    <option value="trilocale">trilocale</option>
                    <option value="quadrivano">quadrivano</option>
                    <option value="altro">altro</option>
                  </select>
                </div>
                <TextField label="Caratteristica (es. vista-montagna)" value={form.caratteristica || ""} onChange={(v) => setForm({ ...form, caratteristica: v })} testid="apt-caratteristica" placeholder="vista-montagna" />
                <div>
                  <label className="text-xs text-fir-deep/60">Slug (URL)</label>
                  <input data-testid="apt-slug" value={form.slug || ""} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })} className="input-alpine w-full mt-0.5" placeholder="vuoto = auto da tipologia-caratteristica" />
                  <span className="text-[10px] text-fir-deep/50 block mt-0.5">Solo minuscole, numeri e trattini. URL: /appartamenti/{form.localita || "dimaro-folgarida"}/{form.slug || "(auto)"}</span>
                </div>
              </div>
              <div className="col-span-2">
                <Label>Descrizione</Label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-alpine w-full h-24" />
              </div>
              {form.type !== "hotel" && (
                <NumField label="Prezzo base €/persona/notte" value={form.base_price} onChange={(v) => setForm({ ...form, base_price: v })} testid="apt-price" />
              )}
              <NumField label="Ospiti max" value={form.max_guests} onChange={(v) => setForm({ ...form, max_guests: v })} />
              <NumField label="Camere" value={form.bedrooms} onChange={(v) => setForm({ ...form, bedrooms: v })} />
              <NumField label="Bagni" value={form.bathrooms} onChange={(v) => setForm({ ...form, bathrooms: v })} />
              {form.type !== "hotel" && (
                <TextField label="ID alloggio Smoobu" span value={form.smoobu_id} onChange={(v) => setForm({ ...form, smoobu_id: v })} testid="apt-smoobu-id" placeholder="es. 1234567" />
              )}
              <div className="col-span-2"><Label>Servizi (separati da virgola)</Label>
                <input value={csv(form.amenities)} onChange={(e) => setForm({ ...form, amenities: e.target.value })} className="input-alpine w-full" /></div>
              <div className="col-span-2"><Label>Regole della casa (separate da virgola)</Label>
                <input value={csv(form.house_rules)} onChange={(e) => setForm({ ...form, house_rules: e.target.value })} className="input-alpine w-full" /></div>

              <TextField label="Codice CIN (mostrato nella descrizione)" span value={form.cin || ""} onChange={(v) => setForm({ ...form, cin: v })} testid="apt-cin" placeholder="IT022079B4XXXXXXXXXX" />
              <TextField label="Indirizzo (mostrato sul sito)" span value={form.address || ""} onChange={(v) => setForm({ ...form, address: v })} testid="apt-address" placeholder="Via delle Dolomiti 1, 38025 Dimaro Folgarida (TN)" />
              <NumField label="Latitudine (opzionale)" value={form.latitude ?? ""} onChange={(v) => setForm({ ...form, latitude: v })} testid="apt-lat" />
              <NumField label="Longitudine (opzionale)" value={form.longitude ?? ""} onChange={(v) => setForm({ ...form, longitude: v })} testid="apt-lng" />
              <div className="col-span-2 -mt-2 text-xs text-fir-deep/50">
                Suggerimento: su Google Maps clic destro sul punto esatto → copia le coordinate (es. 46.3167, 10.8783) e incollale in Latitudine/Longitudine per un segnaposto preciso.
              </div>

              <div className="col-span-2 border-t border-stone pt-4 mt-2">
                <Label>Messaggio WhatsApp preimpostato (per questa struttura)</Label>
                <textarea data-testid="apt-whatsapp-template" value={form.whatsapp_template || ""}
                  onChange={(e) => setForm({ ...form, whatsapp_template: e.target.value })}
                  rows={4} className="input-alpine w-full"
                  placeholder="Ciao {nome}! Grazie per aver prenotato {struttura} dal {checkin} al {checkout}. Sono a disposizione per il check-in e qualsiasi informazione. A presto!" />
                <p className="text-xs text-fir-deep/50 mt-1.5">
                  Segnaposto disponibili: <b>{"{nome}"}</b>, <b>{"{struttura}"}</b>, <b>{"{checkin}"}</b>, <b>{"{checkout}"}</b>, <b>{"{ospiti}"}</b>, <b>{"{totale}"}</b>. Verranno compilati automaticamente col tasto WhatsApp nelle Prenotazioni.
                </p>
              </div>

              <div className="col-span-2 border-t border-stone pt-4 mt-2">
                <Label>Account di pagamento (opzionale, per questa struttura)</Label>
                <p className="text-xs text-fir-deep/50 mt-1 mb-3">
                  Lascia vuoto per usare l'account <b>globale</b> predefinito (quello attuale). Compila per incassare i pagamenti di questa struttura su un tuo account Stripe/PayPal dedicato.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <span className="text-[10px] uppercase tracking-wide text-fir-deep/50">Stripe · Chiave segreta {form.stripe_secret_set ? "· configurata ✓" : ""}
                      {form.stripe_secret_set && (
                        <button type="button" data-testid="apt-stripe-remove" onClick={() => setForm({ ...form, stripe_secret_key: "__REMOVE__", stripe_secret_set: false })}
                          className="ml-2 text-red-500 hover:text-red-700 normal-case">Rimuovi</button>
                      )}
                    </span>
                    <input data-testid="apt-stripe-secret" type="password" autoComplete="new-password"
                      value={form.stripe_secret_key === "__REMOVE__" ? "" : (form.stripe_secret_key || "")} onChange={(e) => setForm({ ...form, stripe_secret_key: e.target.value })}
                      placeholder={form.stripe_secret_key === "__REMOVE__" ? "Verrà rimossa al salvataggio (userà l'account globale)" : (form.stripe_secret_set ? "•••• già configurata (lascia vuoto per non modificare)" : "sk_live_... oppure sk_test_...")}
                      className="input-alpine w-full mt-0.5" />
                  </div>
                  <div>
                    <span className="text-[10px] uppercase tracking-wide text-fir-deep/50">PayPal · Client ID</span>
                    <input data-testid="apt-paypal-client" value={form.paypal_client_id || ""} onChange={(e) => setForm({ ...form, paypal_client_id: e.target.value })} className="input-alpine w-full mt-0.5" placeholder="Client ID PayPal" />
                  </div>
                  <div>
                    <span className="text-[10px] uppercase tracking-wide text-fir-deep/50">PayPal · Ambiente</span>
                    <select data-testid="apt-paypal-mode" value={form.paypal_mode || "sandbox"} onChange={(e) => setForm({ ...form, paypal_mode: e.target.value })} className="input-alpine w-full mt-0.5">
                      <option value="sandbox">Sandbox (test)</option>
                      <option value="live">Live (reale)</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <span className="text-[10px] uppercase tracking-wide text-fir-deep/50">PayPal · Secret {form.paypal_secret_set ? "· configurato ✓" : ""}</span>
                    <input data-testid="apt-paypal-secret" type="password" autoComplete="new-password"
                      value={form.paypal_secret || ""} onChange={(e) => setForm({ ...form, paypal_secret: e.target.value })}
                      placeholder={form.paypal_secret_set ? "•••• già configurato (lascia vuoto per non modificare)" : "PayPal secret"}
                      className="input-alpine w-full mt-0.5" />
                  </div>
                </div>
              </div>

              <div className="col-span-2 border-t border-stone pt-4 mt-2">
                <Label>Info riservate per l'ospite (WiFi, codici accesso)</Label>
                <p className="text-xs text-fir-deep/50 mt-1 mb-3">
                  Visibili SOLO all'ospite che ha prenotato, tramite l'Assistente AI, da <b>2 giorni prima dell'arrivo fino alla partenza</b>. Vengono anche inviate via email automatica 2 giorni prima del check-in. Non appaiono mai sul sito pubblico.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-[10px] uppercase tracking-wide text-fir-deep/50">Nome rete WiFi</span>
                    <input data-testid="gi-wifi-name" value={(form.guest_info || {}).wifi_name || ""} onChange={(e) => setGI({ wifi_name: e.target.value })} className="input-alpine w-full mt-0.5" />
                  </div>
                  <div>
                    <span className="text-[10px] uppercase tracking-wide text-fir-deep/50">Password WiFi</span>
                    <input data-testid="gi-wifi-pass" value={(form.guest_info || {}).wifi_password || ""} onChange={(e) => setGI({ wifi_password: e.target.value })} className="input-alpine w-full mt-0.5" />
                  </div>
                  <div className="col-span-2">
                    <span className="text-[10px] uppercase tracking-wide text-fir-deep/50">Codice cassetta chiavi / portone</span>
                    <input data-testid="gi-keybox" value={(form.guest_info || {}).keybox_code || ""} onChange={(e) => setGI({ keybox_code: e.target.value })} className="input-alpine w-full mt-0.5" />
                  </div>
                  <div className="col-span-2">
                    <span className="text-[10px] uppercase tracking-wide text-fir-deep/50">Istruzioni self check-in</span>
                    <textarea data-testid="gi-checkin" value={(form.guest_info || {}).checkin_instructions || ""} onChange={(e) => setGI({ checkin_instructions: e.target.value })} rows={2} className="input-alpine w-full mt-0.5" />
                  </div>
                  <div className="col-span-2">
                    <span className="text-[10px] uppercase tracking-wide text-fir-deep/50">Parcheggio</span>
                    <input data-testid="gi-parking" value={(form.guest_info || {}).parking_info || ""} onChange={(e) => setGI({ parking_info: e.target.value })} className="input-alpine w-full mt-0.5" />
                  </div>
                  <div className="col-span-2">
                    <span className="text-[10px] uppercase tracking-wide text-fir-deep/50">Altre info</span>
                    <textarea data-testid="gi-other" value={(form.guest_info || {}).other_info || ""} onChange={(e) => setGI({ other_info: e.target.value })} rows={2} className="input-alpine w-full mt-0.5" />
                  </div>
                </div>
              </div>

              {form.type === "hotel" && (
                <div className="col-span-2 border-t border-stone pt-4 mt-1">
                  <div className="flex items-center justify-between mb-3">
                    <Label>Tipologie di camera</Label>
                    <button data-testid="add-room-btn" onClick={addRoom} className="text-fir hover:text-gold flex items-center gap-1 text-sm"><Plus size={14} /> Aggiungi camera</button>
                  </div>
                  <div className="space-y-4">
                    {(form.room_types || []).map((rt, idx) => (
                      <div key={rt.id} data-testid="room-type-row" className="rounded-2xl border border-stone bg-white p-4">
                        <div className="flex items-center justify-between mb-2">
                          <input data-testid="room-name" placeholder="Nome camera (es. Doppia)" value={rt.name} onChange={(e) => updateRoom(idx, { name: e.target.value })} className="input-alpine flex-1 mr-2" />
                          <button data-testid="remove-room-btn" onClick={() => removeRoom(idx)} className="text-red-500 hover:text-red-700"><Trash2 size={16} /></button>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <SmallNum label="Ospiti max" value={rt.max_guests} onChange={(v) => updateRoom(idx, { max_guests: v })} />
                          <SmallNum label="Quantità" value={rt.quantity} onChange={(v) => updateRoom(idx, { quantity: v })} testid="room-quantity" />
                          <SmallNum label="Base €/pers" value={rt.base_price} onChange={(v) => updateRoom(idx, { base_price: v })} testid="room-base" />
                          <SmallNum label="Min €/pers" value={rt.min_price} onChange={(v) => updateRoom(idx, { min_price: v })} />
                          <SmallNum label="Max €/pers" value={rt.max_price} onChange={(v) => updateRoom(idx, { max_price: v })} />
                          <div>
                            <span className="text-[10px] uppercase tracking-wide text-fir-deep/50">Smoobu ID</span>
                            <input value={rt.smoobu_id || ""} onChange={(e) => updateRoom(idx, { smoobu_id: e.target.value })} className="input-alpine w-full mt-0.5" />
                          </div>
                        </div>
                        <div className="mt-3">
                          <ImageUploader label="Foto camera" value={rt.images || []} onChange={(v) => updateRoom(idx, { images: v })} testid={`room-images-${idx}`} />
                        </div>
                      </div>
                    ))}
                    {(form.room_types || []).length === 0 && <p className="text-sm text-fir-deep/50">Nessuna camera. Aggiungine almeno una.</p>}
                  </div>
                </div>
              )}

              <div className="col-span-2">
                <ImageUploader label={form.type === "hotel" ? "Foto hotel (copertina/gallery)" : "Foto appartamento"} value={form.images} onChange={(v) => setForm({ ...form, images: v })} testid="apt-images-upload" />
              </div>
              <div className="col-span-2">
                <ImageUploader label="Immagini 360° (panoramiche equirettangolari)" value={form.panoramas} onChange={(v) => setForm({ ...form, panoramas: v })} testid="apt-panoramas-upload" />
                <p className="text-xs text-fir-deep/50 mt-1">Carica foto panoramiche a 360° (formato equirettangolare 2:1) per il tour virtuale.</p>
              </div>
            </div>
            <button data-testid="save-apartment-btn" onClick={save} disabled={saving} className="gold-btn w-full py-3 rounded-xl mt-6 flex items-center justify-center gap-2">
              {saving ? <Loader2 className="animate-spin" size={18} /> : "Salva struttura"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const Label = ({ children }) => <label className="text-xs uppercase tracking-wide text-fir-deep/60">{children}</label>;
function TextField({ label, value, onChange, span, testid, placeholder }) {
  return <div className={span ? "col-span-2" : ""}><Label>{label}</Label>
    <input data-testid={testid} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="input-alpine w-full mt-1" /></div>;
}
function NumField({ label, value, onChange, testid }) {
  return <div><Label>{label}</Label>
    <input data-testid={testid} type="number" value={value} onChange={(e) => onChange(Number(e.target.value))} className="input-alpine w-full mt-1" /></div>;
}
function SmallNum({ label, value, onChange, testid }) {
  return <div><span className="text-[10px] uppercase tracking-wide text-fir-deep/50">{label}</span>
    <input data-testid={testid} type="number" value={value} onChange={(e) => onChange(Number(e.target.value))} className="input-alpine w-full mt-0.5" /></div>;
}
